import { DateValue, getLocalTimeZone, today } from "@internationalized/date"
import { RangeValue } from "@react-types/shared"
import { createFileRoute } from "@tanstack/react-router"
import { useState } from "react"

import Card from "@/globalComponents/atoms/Card"
import Heading from "@/globalComponents/atoms/Heading"
import Text from "@/globalComponents/atoms/Text"
import Calendar, {
  type CalendarDateStatus,
} from "@/globalComponents/molecules/Calendar"
import DateRangeField from "@/globalComponents/molecules/DateRangeField"
import { useTheme } from "@/utilities/hooks/useTheme"

export const Route = createFileRoute(
  "/qr/$qrLookupID/dev/molecules/range-calendar"
)({
  staticData: {
    pageName: "RangeCalendar",
  },
  component: RouteComponent,
})

function RouteComponent() {
  const { locationInformation } = useTheme()
  const timeZone = locationInformation?.timeZone || getLocalTimeZone()
  const todayDate = today(timeZone)

  const [range, setRange] = useState<RangeValue<DateValue> | null>(null)
  const [single, setSingle] = useState<DateValue | null>(null)

  /*
    One day of each status that rules out a range start, so the rule can be
    exercised by hand. Held in state to keep one map for the life of the page:
    the calendar reads it by identity.
  */
  const [statusMap] = useState(
    () =>
      new Map<string, CalendarDateStatus>(
        (
          [
            [2, "unavailable"],
            [3, "unavailable"],
            [8, "blocked"],
            [14, "disabled"],
          ] as const
        ).map(([days, status]) => [todayDate.add({ days }).toString(), status])
      )
  )

  return (
    <div style={{ display: "grid", gap: "var(--space-4)" }}>
      <Card>
        <Heading level={1} as={2}>
          RangeCalendar
        </Heading>
        <Text>
          Contiguous start-to-end date selection. Drag or click a start date,
          then an end date. Selecting the same day twice produces a single-day
          range. The closed, sold out and check back days below may be spanned
          or ended on, but a range may not start on one.
        </Text>
      </Card>

      <Card>
        <Heading level={2} as={3}>
          Selected
        </Heading>
        <Text>{range ? `${range.start} → ${range.end}` : "none"}</Text>
      </Card>

      <Card>
        <DateRangeField value={range} placeholder="Select dates" />
      </Card>

      <Card>
        <Calendar
          mode="range"
          value={range}
          onChange={setRange}
          minValue={todayDate}
          statusMap={statusMap}
          aria-label="Reservation dates"
        />
      </Card>

      <Card>
        <Heading level={2} as={3}>
          Single date, for comparison
        </Heading>
        <Calendar
          value={single}
          onChange={setSingle}
          minValue={todayDate}
          aria-label="Reservation date"
        />
      </Card>
    </div>
  )
}
